import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, Image, TouchableOpacity, ScrollView, Linking } from 'react-native-web';
import { useRoute } from '@react-navigation/native';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import * as Animatable from 'react-native-animatable';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Lottie from 'react-lottie';
import Feather from 'react-native-vector-icons/dist/Feather';

// Custom Components
import { Button, NavBar, SceneCateringTagItem, TextBox } from '../../components';
import LoadingView from '../../components/component_loading_view';
import DetailTimeSection from '../../components/component_catering_time_section';
import ReminderActionArea from '../../components/component_catering_action_area';
import { CateringFeedbackForm } from '../../components/component_catering_feedback_form';

// Lotties
import * as CateringLoadingLottie from '../../lotties/lottie_loading_catering.json';

// Utils
import { showAlert, showAppError, numberWithCommas, isTestAccount, formatPhoneNumber, getData } from '../../utils/util_helpers';
import { getAcceptedMessage, getRequestType, isCateringFilled, isCateringMissed, isFeedbackGiven, isFeedbackNeeded, isHired, isNotHired } from '../../utils/util_catering';
import { promptToAddToCalendar } from '../../utils/util_events';

// APIs
import {
    acceptCateringRequest,
    declineCateringRequest,
    getCateringDetail,
    markAsHired,
    markClientUnresponsive,
    submitCateringFeedback,
    updateCateringReminderStatus,
    updateExternalCateringLead,
    sendInviteExternalCateringLead
} from '../../modules/api_catering';
import { checkDate } from '../../modules/converters';

// Configs
import GLOBAL from '../../configs/config_global';
import { setGlobalState, STATE_KEYS, useGlobalState } from '../../configs/config_state';

// Navigation
import * as RootNavigation from '../../navigation/RootNavigation';
import routeKeys from '../../navigation/RouteKeys';
import { DatePickerModal, TimePickerModal } from 'react-native-paper-dates';
import RouteKeys from '../../navigation/RouteKeys';
import CateringStatusAlert from './component_catering_alert_modal';
import CateringCannotMakeIt from './component_catering_not_make_it_modal';

export function CateringDetails() {
    const routeParams = useRoute()?.params || {};
    const {
        onAccept = () => {},
        onDecline = () => {},
        onHired = () => {},
        onFeedbackSubmitted = () => {},
        onLeadNotFound = () => {},
        onMyLeadUpdated = () => {}
    } = routeParams;
    const propCateringData = routeParams.cateringData || {};

    // region Values
    const cateringTypeData = {
        1: {
            name: 'Food Truck Service',
            description: 'This is the most common choice and can be done with the host or attendees paying. Customers order directly from the truck, either with a limited menu or the truck\'s full menu. Works best when customers order throughout the event and not all at once.'
        },
        2: {
            name: 'Buffet',
            description: 'Food trucks set up a buffet either in front of the truck or wherever you prefer. Works best for events where the host is paying, there is a set menu and price per person, and you want to feed everyone in a relatively short amount of time.'
        },
        3: {
            name: 'Drop Off',
            description: 'Think more of a traditional catering option. The food truck will drop off the food to be eaten on site, no truck required. Works best for host paying events such as office lunches or catering smaller events.'
        },
        4: {
            name: 'Unsure',
            description: 'Check this box if you aren\'t sure what would work best for your unique event. The Truckster catering team will reach out to help you understand your options.'
        }
    };
    // endregion

    // region Refs
    const feedbackForm = useRef(null);
    const mainScrollView = useRef(null);
    const alertModal = useRef(null);
    const cantMakeItModal = useRef(null);
    // endregion

    // region Global State
    const [globalTruckData] = useGlobalState(STATE_KEYS.TRUCK_DATA);
    const [isDevMode] = useGlobalState(STATE_KEYS.IS_DEV);
    const [inScreenModalShowing] = useGlobalState(STATE_KEYS.IN_SCREEN_MODAL_SHOWING);
    const [accountSettingsKey] = useGlobalState(STATE_KEYS.ACCOUNT_SETTINGS_SCREEN_KEY);
    // endregion

    // region Local State
    const [cateringData, setCateringData] = useState(propCateringData);
    const [isLoadingAction, setLoadingAction] = useState(false);
    const [isLoadingDetails, setLoadingDetails] = useState(true);
    const [isSubmittingFeedback, setSubmittingFeedback] = useState(false);
    const [photoCarouselOpened, setPhotoCarouselOpened] = useState(false);
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

    const [vendorNote, setVendorNote] = useState('');
    const [lastContactDate, setLastContactDate] = useState(null);
    const [showDateSelector, setShowDateSelector] = useState(false);
    const [showTimeSelector, setShowTimeSelector] = useState(false);
    const [savingMyLeadData, setSavingMyLeadData] = useState(false);
    const [sendingInvite, setSendingInvite] = useState(false);

    const [feedbackExpanded, setFeedbackExpanded] = useState(false);
    const [feedbackViewHeight, setFeedbackViewHeight] = useState(0);

    const [pendingAccountSettingsGoTo, setPendingAccountSettingsGoTo] = useState(null);
    // endregion

    // region Component Mounting
    useEffect(() => {
        setTimeout(() => goFetchCateringDetail(), 1000);
    }, []);
    // endregion

    // region Watcher - Account Settings Go To
    useEffect(() => {
        if (pendingAccountSettingsGoTo && accountSettingsKey) {
            setTimeout(() => RootNavigation.setParams(accountSettingsKey, { goTo: String(pendingAccountSettingsGoTo) }), 500);
            setPendingAccountSettingsGoTo(null);
        }
    }, [accountSettingsKey, pendingAccountSettingsGoTo]);
    // endregion

    // region Function - Get Catering Details
    const saveNewCateringData = (newCateringData) => {
        setCateringData(Object.assign(
            cloneDeep(newCateringData),
            {
                timeObj: checkDate(newCateringData, true),
                last_contact_date: (newCateringData['last_contact_date'] || '').replace('0000-00-00 00:00:00', '')
            }
        ));
        setVendorNote(newCateringData['vendor_note'] || '');
        setLastContactDate(
            (!!newCateringData['last_contact_date'] && (newCateringData['last_contact_date'] !== '0000-00-00 00:00:00')) ?
                moment.utc(newCateringData['last_contact_date']).unix()
                :
                null
        );
    }

    const goFetchCateringDetail = () => {
        const cateringID = cateringData.id;
        setLoadingDetails(true);
        getCateringDetail(globalTruckData?.id, cateringID)
            .then((result) => saveNewCateringData(result))
            .catch((error) => {
                RootNavigation.pop();
                if (error.toString() === '404') {
                    onLeadNotFound(cateringID);
                    showAlert({ text: `This lead is no longer available or has been removed from the system.<br/><br/>If you believe this is a mistake, please contact the Truckster team with this ID: ${cateringID}` });
                }
                else showAppError(error, () => {}, true);
            })
            .finally(() => setLoadingDetails(false));
    }
    // endregion

    // region Function - Update My Lead Data
    const goUpdateMyLeadData = () => {
        setSavingMyLeadData(true);
        const newContactDate = lastContactDate ? moment.unix(lastContactDate).utc().format('YYYY-MM-DD HH:mm:ss') : undefined;
        updateExternalCateringLead(globalTruckData.id, {
            'id': cateringData.id,
            'vendor_note': vendorNote,
            'last_contact_date': newContactDate
        })
            .then(() => {
                const newCateringData = cloneDeep(cateringData);
                newCateringData['vendor_note'] = vendorNote;
                newCateringData['last_contact_date'] = newContactDate;
                setCateringData(newCateringData);
            })
            .catch((error) => showAppError(error))
            .finally(() => setSavingMyLeadData(false));
    };
    // endregion

    // region Function - Respond to Confirmation
    const respondToConfirmationFunc = (isConfirmed = false, declineReason = '', usingCateringData = null) => {
        setLoadingAction(true);
        updateCateringReminderStatus(globalTruckData?.id, cateringData.id, isConfirmed ? 1 : 2, declineReason)
            .then(() => {
                const newCateringData = cloneDeep(usingCateringData || cateringData);
                newCateringData['confirmed_status'] = isConfirmed ? 1 : 2;

                if (!isConfirmed) {
                    if (!cateringData['source']) {
                        showAlert({ text: `We are now working on finding your replacement. Please contact the Truckster Team.` });
                    }
                    else {
                        cantMakeItModal.current?.showModal();
                    }
                }

                setCateringData(newCateringData);

                // Has to have timeout on this setParams (because this screen is nested) or else infinite loop will happen
                setTimeout(() => setGlobalState(STATE_KEYS.TRIGGER_CATERING_RESPONDED, cloneDeep(newCateringData)), 500);
            })
            .catch((error) => showAppError(error, () => {}, true))
            .finally(() => setLoadingAction(false));
    }

    const respondToConfirmation = (isConfirmed = false) => {
        if (isConfirmed) {
            showAlert({
                text: `You are responding that you ${isPastStartTime ? 'were at' : 'will be able to make it to'} the catering event`,
                cancelButton: { text: 'Cancel' },
                confirmButton: {
                    text: 'Confirm',
                    color: GLOBAL.COLOR.MAIN,
                    onPress: () => respondToConfirmationFunc(true)
                }
            });
        }
        else {
            if (!cateringData['source']) {
                showAlert({
                    text: 'Please let us know why not?',
                    showTextInput: true,
                    cancelButton: { text: 'Cancel' },
                    confirmButton: {
                        text: 'Submit',
                        color: GLOBAL.COLOR.MAIN,
                        onPress: (text) => respondToConfirmationFunc(false, text)
                    }
                });
            }
            else {
                showAlert({
                    text: `Confirm that you ${isPastStartTime ? 'did not' : 'will NOT be able to '} make it to the catering event?`,
                    cancelButton: { text: 'Cancel' },
                    confirmButton: {
                        text: 'Confirm',
                        color: GLOBAL.COLOR.MAIN,
                        onPress: (text) => respondToConfirmationFunc(false, 'External Lead - No Response')
                    }
                });
            }
        }
    }
    // endregion

    // region Function - Decline Request
    const declineRequest = () => {
        showAlert({
            text: 'Are you sure you want to decline this catering request?',
            cancelButton: { text: 'Cancel' },
            confirmButton: {
                text: 'Confirm',
                color: GLOBAL.COLOR.MAIN,
                onPress: () => {
                    setLoadingAction(true);
                    declineCateringRequest(globalTruckData?.id, cateringData.id)
                        .then(() => {
                            onDecline(cateringData.id);
                            RootNavigation.pop();
                        })
                        .catch((error) => showAppError(error, () => {}, true))
                        .finally(() => setLoadingAction(false));
                }
            }
        });
    }
    // endregion

    // region Function - Accept Request
    const acceptRequest = () => {
        showAlert({
            text: 'Confirm accepting this catering request?',
            cancelButton: { text: 'Cancel' },
            confirmButton: {
                text: 'Confirm',
                color: GLOBAL.COLOR.MAIN,
                onPress: () => {
                    // If user doesn't have any saved payment method, ask them to add one
                    if (!GLOBAL.DATA.USER['total_stripe_cards']) {
                        showAlert({
                            text: (
                                'Truckster charges 10% of sales (excluding tips and tax) for all events booked through Truckster.' +
                                `<br/>Don't worry, if you don't get hired, there is no charge.` +
                                '<br/><br/><b>You must enter a payment method to gain access to this opportunity.</b>'
                            ),
                            cancelButton: {
                                text: 'OK'
                            },
                            confirmButton: {
                                text: 'Add Payment Method',
                                color: GLOBAL.COLOR.MAIN,
                                onPress: () => {
                                    RootNavigation.jumpTo(RouteKeys.ACCOUNT_SETTINGS_STACK);
                                    setPendingAccountSettingsGoTo('payment-settings');
                                }
                            }
                        });
                        return;
                    }

                    setLoadingAction(true);
                    acceptCateringRequest(globalTruckData?.id, cateringData.id)
                        .then(() => {
                            // Refresh details
                            goFetchCateringDetail();

                            // Callback
                            onAccept(cateringData.id);

                            // Show alert
                            getData('cateringAcceptAlertShown')
                                .then((result) => {
                                    console.log('Here', result);
                                    if (result) {
                                        showAlert({
                                            text: getAcceptedMessage(result),
                                            confirmButton: {
                                                text: 'Send Proposal',
                                                color: GLOBAL.COLOR.MAIN,
                                                onPress: _onSendProposalPressed
                                            },
                                            thirdButton: {
                                                text: 'Call Client',
                                                color: GLOBAL.COLOR.BASIC.UI_BLUE,
                                                onPress: _onCallClientPressed
                                            },
                                            cancelButton: {
                                                text: 'Do This Later'
                                            }
                                        });
                                    }
                                    else alertModal.current?.showModal(true);
                                });
                        })
                        .catch((error) => {
                            if (error?.toString().toLowerCase().includes('this event has already been claimed') || error === 422) {
                                showAlert({
                                    text: error,
                                    confirmButton: {
                                        text: 'OK',
                                        color: GLOBAL.COLOR.MAIN,
                                        onPress: () => RootNavigation.pop()
                                    }
                                });
                                onDecline(cateringData.id);
                            }
                            else showAppError(error, () => {}, true);
                        })
                        .finally(() => setLoadingAction(false));
                }
            }
        });
    }
    // endregion

    // region Function - Mark Request as Hired
    const executeMarkHired = (yes = true) => {
        setLoadingAction(true);
        markAsHired(globalTruckData?.id, cateringData.id, yes)
            .then(() => {
                const tempData = cloneDeep(cateringData);
                const fieldName = yes ? 'hired_trucks' : 'not_hired_trucks';

                if (Array.isArray(tempData[fieldName])) {
                    tempData[fieldName].push(globalTruckData);
                }
                else {
                    tempData[fieldName] = [];
                    tempData[fieldName].push(globalTruckData);
                }

                // Callback to parent
                onHired(cloneDeep(tempData), yes);

                // Confirmation
                if (yes) {
                    // If marking hired for a past event, also automatically confirm it
                    if (tempData.timeObj?.startTime < moment().unix()) {
                        respondToConfirmationFunc(true, '', cloneDeep(tempData)); // (New catering local state data will be updated here)
                    }
                    // If the event is in the future, ask user to add to calendar
                    else {
                        goAddToCalendar();

                        // Save to state
                        setCateringData(cloneDeep(tempData));
                    }
                }
            })
            .catch((error) => showAppError(error, () => {}, true))
            .finally(() => setLoadingAction(false));
    }

    const showHiredConfirmation = (yes = true) => {
        showAlert({
            text: 'Confirm that your food truck was ' + (yes ? '' : 'NOT ') + 'hired for this catering lead?',
            cancelButton: { text: 'Cancel' },
            confirmButton: {
                text: 'Confirm',
                color: GLOBAL.COLOR.MAIN,
                onPress: () => executeMarkHired(yes)
            }
        });
    }

    const goMarkAsHired = (yes = true) => {
        if (yes) {
            getData('cateringHiredAlertShown')
                .then((result) => {
                    if (result) {
                        showHiredConfirmation(true);
                    }
                    else alertModal.current?.showModal(false);
                });
        }
        else showHiredConfirmation(false);
    }
    // endregion

    // region Function - Mark Client Non-responsive
    const goMarkClientNonResponsive = () => {
        showAlert({
            text: 'Confirm marking client as non-responsive?',
            cancelButton: { text: 'Cancel' },
            confirmButton: {
                text: 'Confirm',
                color: GLOBAL.COLOR.MAIN,
                onPress: () => {
                    setLoadingAction(true);
                    markClientUnresponsive(globalTruckData?.id, cateringData.id)
                        .then(() => {
                            showAlert({ text: 'Thank you. Our sales team is now working on contacting the client as well' });
                            RootNavigation.pop();
                        })
                        .catch((error) => showAppError(error))
                        .finally(() => setLoadingAction(false));
                }
            }
        });
    };
    // endregion

    // region Function - Add to Calendar
    const goAddToCalendar = () => {
        const calendarNote = (
            `Truckster Catering Lead` +
            `\n---` +
            '\n' +
            `\n${cateringData['occasion']}` +
            `\nrequested by ${cateringData['name']}` +
            `\non ${moment.utc(cateringData['created_at']).format('MMMM Do, YYYY')}` +
            '\n' +
            `\nClient Email: ${cateringData.email}` +
            `\nClient Phone: ${formatPhoneNumber((cateringData.phone?.toString() || '').replace('+1', '')) || cateringData['phone']}` +
            '\n' +
            `\nPaying by: ${cateringData['paying'] === 1 ? 'Host' : 'Participants'}` +
            `\n${cateringData['paying'] === 1 ? 'Budget' : 'Minimum'}: ${cateringData['budget'] === 1 ? '$0' : '$' + numberWithCommas(cateringData['budget'])}` +
            `\nGuests: ${cateringData['participants']}` +
            `\nEvent Type: ${cateringTypeData[Number(cateringData['kind'] || 4)].name}` +
            `\nRequest Type: ${getRequestType(cateringData, globalTruckData.id)}` +
            '\n' +
            `\nClient Notes: ${cateringData['comment'] || '(None)'}`
        );

        promptToAddToCalendar({
            title: `${cateringData['occasion']} - ${cateringData.name}`,
            description: calendarNote,
            location: getMapAddress(),
            startTime: cateringData.timeObj.startTime,
            endTime: cateringData.timeObj.endTime
        });
    }
    // endregion

    // region Function - Get Catering Address
    const getCateringAddress = () => {
        if (!cateringData['address']) return('');

        let splitAddress = cateringData['address'].split(', '),
            addressLine1 = splitAddress[0],
            addressLine2 = cateringData['address'].replace(splitAddress[0] + ', ', ''),
            address = (addressLine1.trim() !== addressLine2.trim()) ? (addressLine1 + '\n' + addressLine2) : addressLine1;

        return(address);
    }
    // endregion

    // region Function - Get Overview Address
    const getOverviewAddress = () => (
        GLOBAL.CITIES.hasOwnProperty(cateringData['user_city']) ?
            GLOBAL.CITIES[cateringData['user_city']].state['short_name'] + ', ' + cateringData['zip_code']
            :
            (cateringData['user_city'] ? cateringData['user_city'] + ', ' : '') + cateringData['zip_code']
    )
    // endregion

    // region Function - Get Map Address
    const getMapAddress = () => {
        if (!cateringData['address']) return('');
        const addressArr = cateringData['address'].split(',');
        return(`${addressArr.length > 1 ? addressArr[1] : addressArr[0]}, ${getOverviewAddress()}`.trim());
    }
    // endregion

    // region Function - Get Given Feedback
    const getGivenFeedback = (customCateringData = null) => {
        const usingCateringData = customCateringData || cateringData;
        const feedbackGiven = isFeedbackGiven(usingCateringData);
        return(
            feedbackGiven ?
                {
                    feedbackWeatherText: usingCateringData['weather'],
                    feedbackSales: usingCateringData['actual_sales'],
                    invoicingSales: usingCateringData['invoicing_sales'],
                    feedbackNotes: usingCateringData['feedback'],
                    feedbackPhotos: usingCateringData['photos'] || '[]'
                }
                :
                {}
        );
    }
    // endregion

    // region Function - Submit Feedback
    const executeFeedbackSubmit = ({ salesCount, invoicingSalesCount, feedbackNotes, feedbackWeather, feedbackPhotos }) => {
        setSubmittingFeedback(true);
        submitCateringFeedback(
            globalTruckData?.id,
            cateringData.id,
            salesCount,
            invoicingSalesCount,
            feedbackNotes,
            feedbackWeather,
            feedbackPhotos,
            false
        )
            .then((updatedCateringData) => {
                const newCateringData = Object.assign(cloneDeep(cateringData), updatedCateringData);
                setCateringData(newCateringData);
                onFeedbackSubmitted(newCateringData);
                showAlert({ text: 'Thank you for your feedback!' });

                // Close feedback form
                feedbackForm.current?.collapseForm();

                // Reload data
                feedbackForm.current?.reloadForm(getGivenFeedback(newCateringData));
            })
            .catch((error) => showAppError(error, () => {}, true))
            .finally(() => setSubmittingFeedback(false))
    }
    // endregion

    // region Function - Get Status
    const getStatus = () => {
        let isCateringOpen = (cateringData['child_status'] === 0),
            isTruckAccepted = (cateringData['child_status'] === 1),
            isFilled = isCateringFilled(cateringData),
            isMissed = isCateringMissed(cateringData),
            isPastStartTime = cateringData?.timeObj?.endTime < moment().unix(),
            isExpired = !isTruckAccepted && (cateringData?.timeObj?.startTime < moment().unix()),
            isTruckHired = isHired(cateringData, globalTruckData.id),
            isTruckNotHired = isNotHired(cateringData, globalTruckData.id),
            statusText = '',
            statusTextColor = 'white',
            statusBackgroundColor = GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY;

        // Is Hired
        if (isTruckHired) {
            statusText = 'Hired';
            statusBackgroundColor = GLOBAL.COLOR.BASIC.UI_GREEN;
        }
        else {
            // Not Hired
            if (isTruckNotHired) {
                statusText = 'Not Hired';
                statusBackgroundColor = GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY;
            }
            else {
                // Accepted
                if (isTruckAccepted) {
                    statusText = 'Accepted';
                    statusBackgroundColor = GLOBAL.COLOR.BASIC.UI_BLUE;
                }
                else {
                    // Missed / Filled
                    if (isMissed || isExpired || isFilled) {
                        statusText = isFilled ? 'Filled' : 'Missed';
                        statusBackgroundColor = GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY;
                    }
                    else {
                        // Pending Request
                        if (isCateringOpen) {
                            statusText = 'Pending Request';
                            statusBackgroundColor = GLOBAL.COLOR.BASIC.UI_YELLOW;

                            if (
                                (
                                    !isFilled &&
                                    ((cateringData['food_truck_id'] === 0) || (cateringData['food_truck_id'] !== 0 && cateringData['extent_number'] > 1)) &&
                                    cateringData['total_accepted_truck'] < GLOBAL.SETTINGS.MAX_CATERING_TRUCKS &&
                                    cateringData['total_accepted_truck'] > 0 &&
                                    cateringData['type'] === 0
                                )
                                &&
                                (!isTruckHired && !isTruckNotHired)
                            ) {
                                let slotLeft = (GLOBAL.SETTINGS.MAX_CATERING_TRUCKS - cateringData['total_accepted_truck']);
                                statusText += `: ${slotLeft} Slot${slotLeft > 1 ? 's' : ''} Left`;
                            }
                        }
                    }
                }
            }
        }

        return({
            isCateringOpen,
            isTruckAccepted,
            isFilled,
            isMissed,
            isPastStartTime,
            isExpired,
            isTruckHired,
            isTruckNotHired,
            statusText,
            statusTextColor,
            statusBackgroundColor
        });
    }
    // endregion

    // region Function - On Send Proposal Pressed
    const _onSendProposalPressed = () => {
        RootNavigation.navigate(routeKeys.CATERING_DETAILS_PROPOSAL, {
            cateringData,
            onProposalTabRequested: () => {
                RootNavigation.pop();
                RootNavigation.jumpTo(routeKeys.PROFILE_STACK);
                RootNavigation.pop(true);
                setTimeout(() => RootNavigation.navigate(routeKeys.MENU, { initialTabIndex: 2 }), 250);
            }
        })
    }
    // endregion

    // region Function - On Call Client Pressed
    const _onCallClientPressed = () => {
        if (!cateringData.phone) {
            showAlert({ text: 'The client did not provide a phone number for contact.' });
            return;
        }

        window.open(`tel:${cateringData.phone}`, '_blank');
    }
    // endregion

    // region Render Action Area
    const renderActionArea = () => {
        const nonResponsiveList = (cateringData['nonresponsive_trucks'] || []);
        const alreadyMarkNonResponsive = nonResponsiveList.find((item) => item.id === globalTruckData?.id);
        return(
            <View style={{ width: '100%', alignSelf: 'center', paddingVertical: 32 }}>
                {
                    cateringData['child_status'] === 0 ?
                        <View style={{ width: '100%', alignSelf: 'center', flexDirection: 'row', alignItems: 'center', paddingHorizontal: 64 }}>
                            <Button
                                onPress={() => declineRequest()}
                                style={{ marginRight: 4, backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY, flex: 1 }}
                                text={'No thanks'}
                                textColor={'black'}
                                textStyle={{ letterSpacing: 0.5, fontWeight: '500' }}
                            />

                            <Button
                                onPress={() => acceptRequest()}
                                style={{ marginLeft: 4, flex: 1 }}
                                text={'I want it!'}
                                textColor={'white'}
                                textStyle={{ letterSpacing: 0.5, fontWeight: '500' }}
                            />
                        </View>
                        :
                        <View style={{ width: '100%', alignSelf: 'center', alignItems: 'center', paddingHorizontal: 64 }}>
                            <Text style={{ color: GLOBAL.COLOR.BASIC.BLACK_TWO, marginBottom: 24, textAlign: 'center', alignSelf: 'stretch', fontWeight: 'bold' }}>
                                Did the client hire your food truck?
                            </Text>

                            {/* Mark Hired/Not Hired */}
                            <View style={{ flexDirection: 'row', alignItems: 'center', alignSelf: 'stretch' }}>
                                <Button
                                    onPress={() => goMarkAsHired(false)}
                                    style={{ backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY, flex: 1, marginRight: 4 }}
                                    text={`Nope`}
                                    textColor={'black'}
                                    textStyle={{ letterSpacing: 0.5, fontWeight: 'bold' }}
                                />

                                <Button
                                    onPress={() => goMarkAsHired(true)}
                                    style={{ backgroundColor: GLOBAL.COLOR.BASIC.UI_GREEN, flex: 1, marginLeft: 4 }}
                                    text={`Yes, they did`}
                                    textColor={'white'}
                                    textStyle={{ letterSpacing: 0.5, fontWeight: 'bold' }}
                                />
                            </View>

                            {/* Divider */}
                            <View style={{ width: 120, height: 1, marginVertical: 16, backgroundColor: GLOBAL.COLOR.DIVIDER }}/>

                            {/* Client Nonresponsive Report */}
                            {
                                alreadyMarkNonResponsive ?
                                    <Text style={{ fontWeight: 'bold', textAlign: 'center', color: GLOBAL.COLOR.BASIC.UI_YELLOW }}>
                                        Client Marked as Nonresponsive
                                    </Text>
                                    :
                                    <Button
                                        onPress={goMarkClientNonResponsive}
                                        style={{ backgroundColor: GLOBAL.COLOR.BASIC.UI_YELLOW, flex: 1 }}
                                        text={'Client is Nonresponsive'}
                                        textColor={'white'}
                                        textStyle={{ letterSpacing: 0.5, fontWeight: 'bold' }}
                                    />
                            }
                        </View>
                }

                {isLoadingAction && <LoadingView isAbsolute={true}/>}
            </View>
        );
    }
    // endregion

    // region Render Address Section
    const renderAddressSection = () => {
        const overviewAddress = getOverviewAddress();
        const isAccepted = (cateringData['child_status'] === 1);
        return(
            <View style={{ alignSelf: 'stretch', flexDirection: 'row', alignItems: isAccepted ? 'flex-start' : 'center', padding: 24, borderBottomWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER }}>
                {/* Icon */}
                <Image
                    source={require('../../images/iconLineLocation.png')}
                    style={{ width: 24, height: 24 }}
                    tintColor={GLOBAL.COLOR.BASIC.BLACK_TWO}
                />

                {/* Address - Only show detail address when accepted */}
                <TouchableOpacity
                    onPress={() => window.open(`https://maps.google.com/?q=${getMapAddress()}`, '_blank')}
                    style={{ flex: 1, marginLeft: 16 }}>
                    <Text style={{ fontWeight: '500', color: GLOBAL.COLOR.BASIC.BLACK_TWO, lineHeight: 28, marginTop: -4 }}>
                        {isAccepted ? getCateringAddress() : overviewAddress}
                    </Text>

                    {
                        isAccepted &&
                        <Text style={{ fontWeight: '400', color: GLOBAL.COLOR.BASIC.NEUTRAL_GREY, marginTop: 6 }}>
                            {overviewAddress}
                        </Text>
                    }
                </TouchableOpacity>
            </View>
        );
    }
    // endregion

    // region Render Last Contact Date Picker
    const renderLastContactDatePicker = () => (
        <>
            <DatePickerModal
                locale={'en'}
                mode={'single'}
                saveLabel={'Save & Choose Time'}
                saveLabelDisabled={true}
                visible={showDateSelector}
                date={lastContactDate ? moment.unix(lastContactDate).toDate() : moment().toDate()}
                validRange={{ endDate: moment().endOf('day').toDate() }}
                onChange={({ date }) => {
                    const newDate = moment.unix(lastContactDate).clone();
                    newDate.set('date', date.getDate());
                    newDate.set('month', date.getMonth());
                    newDate.set('year', date.getFullYear());
                    setLastContactDate(moment(newDate).unix());
                    setShowDateSelector(false);
                    setShowTimeSelector(true);
                }}
                onDismiss={() => setShowDateSelector(false)}
            />

            <TimePickerModal
                locale={'en'}
                mode={'single'}
                defaultInputType={'keyboard'}
                visible={showTimeSelector}
                hours={moment.unix(lastContactDate).hours()}
                minutes={moment.unix(lastContactDate).minutes()}
                use24HourClock={false}
                onConfirm={({ hours, minutes }) => {
                    const newDate = moment.unix(lastContactDate).clone();
                    newDate.set('minutes', minutes);
                    newDate.set('hours', hours);
                    setLastContactDate(moment(newDate).unix());
                    setShowTimeSelector(false);
                }}
                onDismiss={() => setShowTimeSelector(false)}
            />
        </>
    );
    // endregion

    // region Render My Lead Fields
    const renderExtraMyLeadFields = () => {
        const noteChanged = (vendorNote || '').localeCompare((cateringData['vendor_note'] || '').trim()) !== 0;
        const dateChanged = (!cateringData['last_contact_date'] && !lastContactDate) ? false : (moment.utc(cateringData['last_contact_date']).unix() !== lastContactDate);
        const showSaveButton = noteChanged || dateChanged;
        return(
            <View style={{ alignSelf: 'stretch', padding: 24 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    {/* Title */}
                    <Text style={{ fontWeight: '700', flex: 1 }}>
                        Notes (For My Eyes Only)
                    </Text>

                    {/* Save Button */}
                    <Animatable.View
                        animation={showSaveButton ? 'pulse' : null} easing={'ease-out'} iterationCount={'infinite'}
                        style={{ opacity: showSaveButton ? 1 : 0 }}>
                        <TouchableOpacity
                            disabled={!showSaveButton}
                            style={{ borderRadius: 4, backgroundColor: GLOBAL.COLOR.BASIC.UI_BLUE, paddingHorizontal: 16, paddingVertical: 12 }}
                            onPress={() => goUpdateMyLeadData()}>
                            <Text style={{ color: 'white', fontWeight: '500', fontSize: 15 }}>
                                Save Changes
                            </Text>
                        </TouchableOpacity>
                    </Animatable.View>
                </View>

                {/* My Notes */}
                <TextBox
                    imageName={'edit-3'}
                    useFeather={true}
                    imageSize={28}
                    imageColor={GLOBAL.COLOR.BASIC.NEUTRAL_GREY}
                    title={'Your Notes'}
                    multiline={true}
                    contentBoxStyle={{ height: 150, borderColor: noteChanged ? GLOBAL.COLOR.BASIC.UI_BLUE : GLOBAL.COLOR.DIVIDER }}
                    placeholder={'Enter notes here'}
                    value={vendorNote}
                    onChangeText={setVendorNote}
                    autoCapitalize={'none'}
                    autoCorrect={false}
                />

                {/* Last Contact Date */}
                <TextBox
                    imageName={'calendar'}
                    useFeather={true}
                    imageSize={28}
                    imageColor={GLOBAL.COLOR.BASIC.NEUTRAL_GREY}
                    title={'Last Contact Date/Time'}
                    placeholder={'Select Last Contact Date/Time'}
                    containerStyle={{ flex: 1, marginTop: 8 }}
                    contentBoxStyle={{ borderColor: dateChanged ? GLOBAL.COLOR.BASIC.UI_BLUE : GLOBAL.COLOR.DIVIDER }}
                    value={lastContactDate ? moment.unix(lastContactDate).format('h:mm A | MMM D, YYYY') : ''}
                    onPress={() => setShowDateSelector(true)}
                />

                {/* Date Picker */}
                {renderLastContactDatePicker()}
            </View>
        );
    }
    // endregion

    // region Render Detail Item
    const renderDetailItem = (name = '', data = '', hideBorder = false) => (
        <View style={{ alignSelf: 'stretch', flexDirection: 'row', alignItems: 'center', borderBottomWidth: hideBorder ? 0 : 1, borderColor: GLOBAL.COLOR.DIVIDER, paddingVertical: 16 }}>
            {/* Name */}
            <Text style={{ color: GLOBAL.COLOR.BASIC.BROWNISH_GREY, letterSpacing: 0, fontSize: 16, width: 140 }}>
                {name}
            </Text>

            {/* Data */}
            <Text style={{ flex: 1, color: 'black', fontSize: 16, fontWeight: '450', alignSelf: 'stretch' }}>
                {data}
            </Text>
        </View>
    )

    const renderOtherDetailItem = (name = '', isTrue = false, hideBorder = false) => (
        <View style={{ alignSelf: 'stretch', flexDirection: 'row', alignItems: 'center', borderBottomWidth: hideBorder ? 0 : 1, borderColor: GLOBAL.COLOR.DIVIDER, paddingVertical: 16 }}>
            {/* Name */}
            <Text style={{ color: GLOBAL.COLOR.BASIC.BROWNISH_GREY, letterSpacing: 0, fontSize: 16, flex: 1 }}>
                {name}
            </Text>

            {/* Data */}
            <View style={{
                marginLeft: 8, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 10, paddingVertical: 6,
                borderRadius: 4, backgroundColor: isTrue ? GLOBAL.COLOR.BASIC.UI_BLUE : GLOBAL.COLOR.BASIC.GRAY
            }}>
                <Text style={{ fontWeight: 'bold', color: 'white', fontSize: 15 }}>
                    {isTrue ? 'YES' : 'NO'}
                </Text>
            </View>
        </View>
    )
    // endregion

    // region Render Catering Information
    const renderCateringInformation = () => (
        <View style={{ alignSelf: 'stretch', padding: 24, borderBottomWidth: isTrucksterLead ? 1 : 0, borderColor: GLOBAL.COLOR.DIVIDER }}>
            <Text style={{ fontWeight: '700', marginBottom: 16 }}>
                Catering Information
            </Text>

            {renderDetailItem('Paying By', cateringData['paying'] === 1 ? 'Host' : 'Participants')}
            {renderDetailItem(cateringData['paying'] === 1 ? 'Budget' : 'Minimum', (cateringData['budget'] === 1 ? '$0' : '$' + numberWithCommas(cateringData['budget'], false, 0)) + (cateringData['max_budget'] > 0 ? (' - $' + numberWithCommas(cateringData['max_budget'], false, 0)) : '') + (cateringData['hiring_multiple_trucks'] ? ' (per Truck)' : ''))}
            {renderDetailItem('Guest', cateringData['participants']?.toString(), false)}
            {renderDetailItem('Event Type', cateringTypeData[Number(cateringData['kind'] || 4)].name)}
            {renderDetailItem('Request Type', getRequestType(cateringData, globalTruckData.id))}
            {renderDetailItem('Client Note', cateringData.comment || '(None)', true)}
        </View>
    )

    const renderCateringOtherInformation = () => {
        const vendorPhotos =
            cateringData['vendor_photos'] ?
                (
                    typeof cateringData['vendor_photos'] === 'string' ?
                        JSON.parse(cateringData['vendor_photos'])
                        :
                        (Array.isArray(cateringData['vendor_photos']) ? cateringData['vendor_photos'] : [])
                )
                :
                [];

        return(
            <View style={{ alignSelf: 'stretch', padding: 24 }}>
                <Text style={{ fontWeight: '700', marginBottom: 16 }}>
                    Other Information
                </Text>

                {renderOtherDetailItem('Client is Hiring Multiple Trucks', cateringData['hiring_multiple_trucks'])}
                {renderOtherDetailItem('Multi-day or Recurring Event', cateringData['multi_days_or_recurring'], true)}

                {/* Information Photos */}
                {
                    (vendorPhotos).length > 0 &&
                    <View style={{ alignSelf: 'stretch', marginTop: 32 }}>
                        {/* Button */}
                        <Button
                            onPress={() => setPhotoCarouselOpened(true)}
                            backgroundColor={'black'}
                            text={'Information Photos'}
                            textColor={'white'}
                            textStyle={{ fontWeight: '500', fontSize: 16 }}
                        />

                        {/* Lightbox Carousel */}
                        <Lightbox
                            plugins={[Captions]}
                            open={photoCarouselOpened}
                            close={() => setPhotoCarouselOpened(false)}
                            index={currentPhotoIndex}
                            on={{ view: ({ index }) => setCurrentPhotoIndex(index) }}
                            slides={vendorPhotos.map((item, index) => ({
                                key: `imageItem_${item.id}`,
                                src: item['path_webp'] || item['path'],
                                id: item.id,
                                title: `Information Photo ${index + 1} of ${vendorPhotos.length}`,
                                description: item.caption
                            }))}
                            carousel={{ imageFit: 'contain' }}
                            styles={{ container: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
                        />
                    </View>
                }
            </View>
        );
    }
    // endregion

    // region Render Contact Section

    // region Render Quick Button
    const renderQuickButton = (text, image, isImageFile = false, _onPress = () => {}) => {
        const buttonSize = 48, imageSize = 24;
        return(
            <TouchableOpacity
                onPress={_onPress}
                style={{ flex: 1, alignItems: 'center' }}>
                {/* Icon */}
                <View
                    style={{
                        width: buttonSize, height: buttonSize, alignItems: 'center', justifyContent: 'center',
                        backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY, borderRadius: buttonSize / 2
                    }}>
                    {
                        isImageFile ?
                            <Image
                                source={image}
                                style={{ width: imageSize, height: imageSize }}
                            />
                            :
                            <Feather
                                name={image}
                                size={imageSize}
                                color={GLOBAL.COLOR.BASIC.BLACK_TWO}
                            />
                    }
                </View>

                {/* Text */}
                <Text style={{ fontSize: 15, fontWeight: '450', color: GLOBAL.COLOR.BASIC.BLACK_TWO, marginTop: 8, textAlign: 'center' }}>
                    {text}
                </Text>
            </TouchableOpacity>
        );
    }
    // endregion

    const renderContactSection = () => (
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', paddingHorizontal: 24, paddingBottom: 24 }}>
            {/* Email */}
            {renderQuickButton(cateringData.email || 'Email', 'mail', false, () => {
                if (!cateringData.email) {
                    showAlert({ text: 'The client did not provide an email address for contact.' });
                    return;
                }

                window.open(`mailto:${cateringData.email}`, '_blank');
            })}

            {/* Call */}
            {renderQuickButton(cateringData.phone ? formatPhoneNumber(cateringData.phone) : 'Call', 'phone', false, _onCallClientPressed)}

            {/* Send Proposal Button */}
            {
                (isTruckAccepted && !isPastStartTime && !isMissed && !isExpired && !isTruckHired && !isTruckNotHired) &&
                renderQuickButton('Send\nProposal', require('../../images/image_proposal@3x.png'), true, _onSendProposalPressed)
            }

            {/* [My Lead] Send calendar invite */}
            {
                (!isTrucksterLead && isTruckHired && !isTruckNotHired) &&
                renderQuickButton(
                    'Calendar\nInvite',
                    'send',
                    false,
                    () => {
                        showAlert({
                            text: 'Send calendar invite to the client?',
                            cancelButton: { text: 'Cancel' },
                            confirmButton: {
                                text: 'Confirm',
                                color: GLOBAL.COLOR.MAIN,
                                onPress: () => {
                                    setSendingInvite(true);
                                    sendInviteExternalCateringLead(globalTruckData.id, cateringData.id)
                                        .then(() => showAlert({ text: 'Calendar invite sent to the client!' }))
                                        .catch((error) => showAppError(error))
                                        .finally(() => setSendingInvite(false));
                                }
                            }
                        });
                    }
                )
            }

            {/* Add to Calendar */}
            {renderQuickButton(`Add to\nCalendar`, 'calendar', false, () => goAddToCalendar())}
        </View>
    )
    // endregion

    // region MAIN RENDER
    let status = getStatus(),
        isCateringOpen = status.isCateringOpen,
        isTruckAccepted = status.isTruckAccepted,
        isFilled = status.isFilled,
        isMissed = status.isMissed,
        isExpired = status.isExpired,
        isTruckHired = status.isTruckHired,
        isTruckNotHired = status.isTruckNotHired,
        isPastStartTime = status.isPastStartTime,

        showActionArea =
            !isMissed &&
            (!isFilled && !isExpired) &&
            (isCateringOpen || isTruckAccepted) &&
            (!isTruckHired && !isTruckNotHired);

    let isConfirmed = isTruckHired && cateringData['confirmed_status'] === 1,
        isNotComing = isTruckHired && cateringData['confirmed_status'] === 2,
        isClientConfirmed = isTruckHired && !!cateringData['is_confirmed_by_client'],
        isReminder =
            isTruckHired && // Hired
            (cateringData['confirmed_status'] === 0) && // haven't responded to confirmation yet
            (cateringData.timeObj?.startTime >= moment().unix()), // is not already happening or in the past
        showReminderActionArea = isTruckHired && !isLoadingDetails && isReminder;

    const feedbackNeeded = useMemo(() => isFeedbackNeeded(cateringData), [cateringData]);
    const isTrucksterLead = useMemo(() => !cateringData['source'], [cateringData]);

    return(
        <View style={{ width: '100%', flex: 1, backgroundColor: GLOBAL.COLOR.MAIN_BACKGROUND }}>
            {/* Nav Bar */}
            <View style={{ width: '100%' }}>
                <NavBar
                    title={'Catering Event Details'}
                    leftButtonIconName={'keyboard-arrow-left'}
                    leftButtonIconSize={40}
                    leftButtonOnPress={() => RootNavigation.pop()}
                    rightButtonBackgroundColor={GLOBAL.COLOR.BASIC.UI_BLUE}
                    rightButtonColor={'white'}
                    rightButtonTitle={(isTrucksterLead || isLoadingDetails) ? '' : 'Edit Lead'}
                    rightButtonOnPress={() => RootNavigation.navigate(
                        RouteKeys.CATERING_EXTERNAL_LEAD,
                        {
                            myLeadData: cateringData,
                            onMyLeadUpdated: (newData) => {
                                saveNewCateringData(newData);
                                onMyLeadUpdated(Object.assign(cloneDeep(newData), { categoryKey: isTruckAccepted ? 'upcoming' : (isTruckHired ? 'hired' : 'notHired') }));
                            }
                        }
                    )}
                />

                {/* In-Screen Modal Backdrop */}
                <Animatable.View
                    pointerEvents={inScreenModalShowing ? 'auto' : 'none'}
                    animation={inScreenModalShowing ? 'fadeIn' : 'fadeOut'}
                    duration={400}
                    style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
                />
            </View>

            {/* Main Content */}
            {
                isLoadingDetails ?
                    <View style={{ flex: 1, width: '100%', alignItems: 'center', justifyContent: 'center' }}>
                        {/* Lottie */}
                        <Lottie
                            options={{
                                loop: true,
                                autoplay: true,
                                animationData: CateringLoadingLottie,
                                rendererSettings: { preserveAspectRatio: 'xMidYMid slice' }
                            }}
                            width={180}
                            height={180}
                            isStopped={false}
                            isPaused={false}
                        />

                        <Text style={{ color: GLOBAL.COLOR.TEXT_GRAY, fontSize: 16 }}>
                            Loading Catering Data
                        </Text>
                    </View>
                    :
                    <Animatable.View animation={'fadeZoom'} style={{ flex: 1, width: '100%' }}>
                        <ScrollView
                            ref={mainScrollView}
                            style={{ flex: 1, width: '100%' }}
                            contentContainerStyle={{ paddingBottom: feedbackViewHeight + 48 }}>
                            <View style={{ width: '70%', alignSelf: 'center', borderWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER, backgroundColor: 'white', marginTop: 16 }}>
                                {/* My Lead / Catering ID */}
                                <View style={{ alignSelf: 'center', backgroundColor: 'transparent', flexDirection: 'row', alignItems: 'center', marginTop: 24 }}>
                                    {/* Lead Type Tag */}
                                    <SceneCateringTagItem
                                        textSize={13}
                                        style={{ marginTop: 0 }}
                                        text={isTrucksterLead ? 'Truckster Lead' : 'My Lead'}
                                        backgroundColor={isTrucksterLead ? GLOBAL.COLOR.BASIC.DARK_BLUE : GLOBAL.COLOR.BACKGROUND_GREY}
                                        textColor={isTrucksterLead ? 'white' : GLOBAL.COLOR.TEXT_GRAY}
                                    />

                                    {/* Catering ID */}
                                    {
                                        (isDevMode || isTestAccount(GLOBAL.DATA.USER.email)) &&
                                        <SceneCateringTagItem
                                            textSize={13}
                                            style={{ marginTop: 0, alignSelf: 'center' }}
                                            text={'ID: ' + cateringData.id}
                                            backgroundColor={'rgba(0, 117, 219, 0.2)'}
                                            textColor={GLOBAL.COLOR.BASIC.UI_BLUE}
                                        />
                                    }
                                </View>

                                {/* Occasion */}
                                <Text style={{ fontWeight: '900', fontSize: 32, letterSpacing: -0.1, textAlign: 'center', marginTop: 24 }}>
                                    {cateringData['occasion']}
                                </Text>

                                {/* Name */}
                                <Text style={{ color: GLOBAL.COLOR.BASIC.BROWNISH_GREY, letterSpacing: 0.5, fontSize: 18, marginTop: 8, lineHeight: 28, textAlign: 'center' }}>
                                    {
                                        `Requested by ${cateringData.name}${cateringData['company_name'] ? ` | ${cateringData['company_name']}` : ''}` +
                                        (isTrucksterLead ? `\non ${moment.utc(cateringData['created_at']).format('MMMM Do, YYYY')}` : '')
                                    }
                                </Text>

                                {/* Hire Status */}
                                <View style={{ width: '100%', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginTop: 24, marginBottom: 8 }}>
                                    <View style={{ position: 'absolute', height: 1, left: 0, right: 0, backgroundColor: GLOBAL.COLOR.DIVIDER }}/>

                                    {/* Normal Status */}
                                    <SceneCateringTagItem
                                        style={{ marginRight: 0, marginTop: 0, alignSelf: 'center' }}
                                        text={status.statusText}
                                        textColor={status.statusTextColor}
                                        textSize={16}
                                        textWeight={'500'}
                                        backgroundColor={status.statusBackgroundColor}
                                    />

                                    {/* Confirmation Status */}
                                    {
                                        ((isNotComing || isConfirmed) && !isLoadingDetails) &&
                                        <SceneCateringTagItem
                                            style={{ marginRight: 0, marginLeft: 8, marginTop: 0 }}
                                            text={isConfirmed ? 'Truck ✓' : `Not Going`}
                                            textColor={isConfirmed ? 'white' : GLOBAL.COLOR.BASIC.NEUTRAL_GREY}
                                            textSize={16}
                                            textWeight={'500'}
                                            backgroundColor={isConfirmed ? GLOBAL.COLOR.MAIN : GLOBAL.COLOR.BACKGROUND_GREY}
                                        />
                                    }

                                    {/* Client Confirmation Status */}
                                    {
                                        (isClientConfirmed && !isLoadingDetails) &&
                                        <SceneCateringTagItem
                                            style={{ marginRight: 0, marginLeft: 8, marginTop: 0 }}
                                            text={'Client ✓'}
                                            textColor={isConfirmed ? 'white' : GLOBAL.COLOR.BASIC.NEUTRAL_GREY}
                                            textSize={16}
                                            textWeight={'500'}
                                            backgroundColor={GLOBAL.COLOR.BASIC.PURPLE}
                                        />
                                    }
                                </View>

                                {/* Details */}
                                <Animatable.View animation={'fadeIn'} style={{ flex: 1, width: '100%', paddingVertical: 24 }}>
                                    {/* Contact Section */}
                                    {(isTruckAccepted || isTruckHired) && renderContactSection()}

                                    {/* Date/Time */}
                                    <DetailTimeSection eventData={cateringData}/>

                                    {/* Address Section */}
                                    {renderAddressSection()}

                                    {/* Extra Fields for "My Lead" */}
                                    {isTruckAccepted && renderExtraMyLeadFields()}

                                    {/* Catering Information */}
                                    {renderCateringInformation()}
                                    {isTrucksterLead && renderCateringOtherInformation()}
                                </Animatable.View>
                            </View>

                            {/* External Lead Modal */}
                        </ScrollView>
                    </Animatable.View>
            }

            {/* Feedback Underlayer */}
            {
                feedbackExpanded &&
                <View style={{ position: 'absolute', left: 0, right: 0, bottom: 0, top: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)' }} />
            }

            {/* Feedback Area */}
            {
                !isLoadingDetails &&
                <Animatable.View
                    animation={'fadeInUp'} duration={600} delay={500}
                    style={{
                        position: 'absolute', left: '30%', right: '30%', bottom: 24, zIndex: 100,
                        boxShadow: '0px 1px 5px #b0b8b2, 0px -1px 5px #b0b8b2', borderRadius: 16,
                        overflow: 'hidden', backgroundColor: 'white'
                    }}
                    onLayout={(event) => setFeedbackViewHeight(event.nativeEvent.layout.height)}>
                    {/* Feedback Form */}
                    {
                        (feedbackNeeded || isFeedbackGiven(cateringData)) &&
                        <CateringFeedbackForm
                            ref={feedbackForm}
                            isTrucksterLead={isTrucksterLead}
                            onFeedbackExpanded={(expanded) => setFeedbackExpanded(expanded)}
                            onSubmitFeedback={(feedbackData) => executeFeedbackSubmit(feedbackData)}
                            isSendingFeedback={isSubmittingFeedback}
                            givenFeedback={getGivenFeedback()}
                        />
                    }

                    {/* Action Area */}
                    {showActionArea && renderActionArea()}

                    {/* Reminder Action Area */}
                    {
                        showReminderActionArea &&
                        <ReminderActionArea
                            cateringData={cateringData}
                            text={`${isPastStartTime ? 'Did' : 'Will'} you make it to the catering event?`}
                            isLoadingAction={isLoadingAction}
                            onConfirm={() => respondToConfirmation(true)}
                            onDecline={() => respondToConfirmation(false)}
                        />
                    }
                </Animatable.View>
            }

            {/* Accept/Hired Alert Modal */}
            <CateringStatusAlert
                ref={alertModal}
                onHiredPressed={() => executeMarkHired(true)}
                clientPhone={cateringData['phone']}
                onAcceptingSendProposalPressed={_onSendProposalPressed}
            />

            {/* Can't Make It Alert */}
            <CateringCannotMakeIt
                ref={cantMakeItModal}
                clientEmail={cateringData['email']}
                clientPhone={cateringData['phone']}
                clientName={cateringData['name']}
            />

            {/* Loading View */}
            {
                (sendingInvite || savingMyLeadData) &&
                <LoadingView isAbsolute={true} isLoading={true} description={savingMyLeadData ? 'Saving notes' : 'Sending calendar invite...'} />
            }
        </View>
    );
    // endregion
}
